import { useCallback, useEffect, useMemo, useState } from 'react'

import { toast } from '@webapp/components/Notification'
import { useChecklistManager } from '@webapp/core/hooks/useChecklistManager'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type TravelListStatus } from '@webapp/modules/expense/business-logic/types/TravelListStatus'
import { type TravelStatusCounts } from '@webapp/modules/expense/business-logic/types/TravelStatusCounts'
import { type TravelRequestSummary } from '@webapp/modules/expense/entities/TravelRequestSummary'
import messages from './messages'
import { useQueryApprovableTravels } from '../../models'

/** Checkbox + per-row approver pick reported upward to the Feature Set. */
export interface TravelApprovalSelection {
  travelId: number
  approverId: Nullable<number>
}

export interface UseApprovableTravelListPresenterParams {
  status: TravelListStatus
  subscribedApproverId?: number
  isSelectable: boolean
  onSelectionChange: (payload: TravelApprovalSelection[]) => void
  onCountsChange: (counts: TravelStatusCounts) => void
}

export const useApprovableTravelListPresenter = ({
  status,
  subscribedApproverId,
  isSelectable,
  onSelectionChange,
  onCountsChange,
}: UseApprovableTravelListPresenterParams) => {
  const intl = useProcurifyIntl()

  const {
    check,
    clear: clearChecklist,
    checklistState,
  } = useChecklistManager<TravelRequestSummary>({ keyId: 'id' })

  const [approverIdByTravelId, setApproverIdByTravelId] = useState<
    Record<string, Nullable<number>>
  >({})

  const { travels, pagination, isLoading } = useQueryApprovableTravels({
    status,
    subscribedApproverId,
    onCountsChange,
    onError: (err) => {
      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children:
          getMessageFromError(err) ||
          intl.formatMessage(messages.errorToastMessage),
      })
    },
  })

  const handleApproverPick = useCallback(
    (travelId: number, approverId: Nullable<number>) => {
      setApproverIdByTravelId((prev) => ({
        ...prev,
        [String(travelId)]: approverId,
      }))
    },
    []
  )

  // BHV-037: preselect the next approver when exactly one choice exists
  useEffect(() => {
    travels.forEach((travel) => {
      if (
        travel.nextApproverChoices.length === 1 &&
        approverIdByTravelId[String(travel.id)] === undefined
      ) {
        handleApproverPick(travel.id, travel.nextApproverChoices[0].id)
      }
    })
  }, [travels, approverIdByTravelId, handleApproverPick])

  // Propagate the full selection payload whenever checkboxes or picks change
  useEffect(() => {
    const payload: TravelApprovalSelection[] = Object.keys(checklistState.items)
      .filter((key) => checklistState.items[key])
      .map((key) => ({
        travelId: Number(key),
        approverId: approverIdByTravelId[key] ?? null,
      }))

    onSelectionChange(payload)
  }, [checklistState, approverIdByTravelId, onSelectionChange])

  const selectedIds = useMemo(
    () =>
      new Set(
        Object.keys(checklistState.items)
          .filter((key) => checklistState.items[key])
          .map(Number)
      ),
    [checklistState.items]
  )

  const handleToggleSelect = useCallback(
    (travel: TravelRequestSummary, checked: boolean) => {
      check(travel, checked)
    },
    [check]
  )

  const handleToggleSelectAll = useCallback(
    (checked: boolean) => {
      check(
        travels.filter((travel) => travel.isPending),
        checked
      )
    },
    [check, travels]
  )

  const clearSelection = useCallback(() => {
    clearChecklist()
    setApproverIdByTravelId({})
    onSelectionChange([])
  }, [clearChecklist, onSelectionChange])

  const visiblePendingIds = travels
    .filter((travel) => travel.isPending)
    .map((travel) => travel.id)

  const allVisibleSelected =
    visiblePendingIds.length > 0 &&
    visiblePendingIds.every((id) => selectedIds.has(id))

  return {
    data: {
      travels,
      pagination,
    },
    states: {
      isLoading,
      isSelectable,
      selectedIds,
      allVisibleSelected,
      approverIdByTravelId,
    },
    methods: {
      onToggleSelect: handleToggleSelect,
      onToggleSelectAll: handleToggleSelectAll,
      onApproverPick: handleApproverPick,
      clearSelection,
    },
  }
}
